import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCreateCustomer } from "@/api/customers";
import {
	CustomerFormPage,
	type CustomerFormValues,
} from "@/routes/_auth/app/_app/org/$orgId/customers/-components/CustomerFormPage";
import { toCreateCustomerRequest } from "@/routes/_auth/app/_app/org/$orgId/customers/-components/customerFormPayload";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customers/new",
)({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context }) => {
		const token = await context.get_token();
		if (!token) {
			throw new Error("No token available");
		}
		return { token };
	},
	component: NewCustomerPage,
});

function NewCustomerPage() {
	const { orgId } = Route.useParams();
	const { token } = Route.useLoaderData();
	const navigate = useNavigate();

	const createCustomerMutation = useCreateCustomer();

	const handleSubmit = async (values: CustomerFormValues) => {
		try {
			const newCustomer = await createCustomerMutation.mutateAsync({
				token,
				organizationId: orgId,
				data: toCreateCustomerRequest(values),
			});

			navigate({
				to: "/app/org/$orgId/customers/$customerId",
				params: { orgId, customerId: newCustomer.id },
			});
		} catch (error) {
			// The mutation hook raises the error toast; staying on the form with the
			// entered values intact is the only thing left to do here.
			console.error("Error creating customer:", error);
		}
	};

	const handleCancel = () =>
		navigate({ to: "/app/org/$orgId/customers", params: { orgId } });

	return (
		<div className="h-full min-h-0">
			<CustomerFormPage
				customer={null}
				isSubmitting={createCustomerMutation.isPending}
				onSubmit={handleSubmit}
				onCancel={handleCancel}
			/>
		</div>
	);
}
